import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Company, PaginatedCompanies } from '@/types/company'
import type { CompanyFormData } from '@/validators/company'

export interface ListCompanyParams {
  search?: string
  active?: boolean
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListCompanyParams) => ['companies', params] as const,
  detail: (id: string) => ['companies', id] as const,
}

export function useCompanies(params: ListCompanyParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<PaginatedCompanies>('/companies', { params }).then((r) => r.data),
  })
}

export function useCompany(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<Company>(`/companies/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CompanyFormData) =>
      api.post<Company>('/companies', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  })
}

export function useUpdateCompany(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CompanyFormData) =>
      api.put<Company>(`/companies/${id}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['companies'] })
      qc.setQueryData(KEYS.detail(id), updated)
    },
  })
}

export function useDeactivateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<Company>(`/companies/${id}/deactivate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  })
}

export function useActivateCompany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<Company>(`/companies/${id}/activate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  })
}
